import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, KeyRound, Leaf, Loader2, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useSetPassword, useVerifyResetLink } from "@/hooks/useSetPassword";

/**
 * /set-password/:uidb64/:token — landing page for the invite email.
 *
 * On mount we POST /auth/set-password/verify/ to confirm the link
 * is still valid; if it isn't, show a useful error before the user
 * wastes time typing. On submit we POST /confirm/, then bounce to
 * /login on success.
 */
export default function SetPassword() {
  const navigate = useNavigate();
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>();
  const verify = useVerifyResetLink({ uidb64, token });
  const submit = useSetPassword();

  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");

  const linkInvalid = verify.isError;
  const passwordsMismatch = password.length > 0 && confirm.length > 0 && password !== confirm;
  const canSubmit =
    !!uidb64 && !!token &&
    password.length > 0 && password === confirm &&
    !submit.isPending && verify.isSuccess;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    submit.mutate(
      { uidb64: uidb64!, token: token!, password },
      {
        onSuccess: () => {
          // Tiny delay so the success state is readable before redirect.
          setTimeout(() => navigate("/login?invited=1", { replace: true }), 800);
        },
      },
    );
  }

  const submitErr = submit.error as
    | { response?: { data?: { detail?: string; password?: string[] } } }
    | undefined;
  const submitErrMsg = submitErr?.response?.data?.detail
    ?? submitErr?.response?.data?.password?.[0];

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden bg-gradient-to-br from-primary via-emerald-700 to-emerald-900 p-12 text-primary-foreground lg:flex lg:flex-col lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-card/15 backdrop-blur-sm">
            <Leaf className="h-5 w-5" />
          </div>
          <div className="text-sm font-semibold tracking-tight">MoPD Climate Portal</div>
        </div>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Welcome aboard.</h2>
          <p className="mt-2 text-sm text-primary-foreground/80">
            Set a password to finish activating your account.
          </p>
        </div>
        <div className="text-xs text-primary-foreground/60">
          Ministry of Planning &amp; Development · Federal Democratic Republic of Ethiopia
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-6">
          <header className="space-y-1">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
              <KeyRound className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Set your password</h1>
            <p className="text-sm text-muted-foreground">
              Pick a password to activate your account.
            </p>
          </header>

          {/* Verifying link */}
          {verify.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-1/3" />
            </div>
          )}

          {/* Invalid / expired link */}
          {linkInvalid && (
            <div className="space-y-4 rounded-md border border-destructive/30 bg-destructive/10 p-4">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-semibold">This link is invalid or has expired.</div>
                  <p className="mt-1 text-xs text-destructive/90">
                    Ask an admin to send you a new invite, or sign in if you already have a password.
                  </p>
                </div>
              </div>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          )}

          {/* Success — keep the form mounted so it doesn't pop, but show the banner. */}
          {submit.isSuccess && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/15 p-4 text-sm text-emerald-800 dark:text-emerald-300">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">Password set.</div>
                <p className="mt-1 text-xs">Sending you to sign in…</p>
              </div>
            </div>
          )}

          {/* Form */}
          {verify.isSuccess && !submit.isSuccess && (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
                Activating account for{" "}
                <span className="font-semibold text-foreground">{verify.data?.email}</span>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="sp-pw">New password</Label>
                <Input
                  id="sp-pw" type="password" required autoFocus
                  autoComplete="new-password" minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  At least 8 characters. Mix letters, numbers, and symbols.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="sp-confirm">Confirm password</Label>
                <Input
                  id="sp-confirm" type="password" required
                  autoComplete="new-password" minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                />
                {passwordsMismatch && (
                  <p className="text-xs text-destructive">Passwords don't match.</p>
                )}
              </div>

              {submitErrMsg && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {submitErrMsg}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={!canSubmit}>
                {submit.isPending
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Setting password…</>
                  : "Set password & sign in"}
              </Button>
            </form>
          )}

          <div className="text-center text-xs text-muted-foreground">
            Already have a password?{" "}
            <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
